import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles/index"
import Typography from "@material-ui/core/Typography"

import { ACCENT } from "../../styles/cta"
import { TIME_ZONE_GROUPS, zoneHeading, detectZone } from "./timeZones"

const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`

// en-CA formats as YYYY-MM-DD, the same shape as dayKey, but in any zone.
const dayKeyIn = (date, timeZone) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date)

const monthOfKey = (key) => {
  const [y, m] = key.split("-").map(Number)
  return new Date(y, m - 1, 1)
}

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const daysInMonth = (d) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
const sameMonth = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

const GlobeIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
    <mask id="fi-globe">
      <rect width="24" height="24" fill="#000" />
      <circle cx="12" cy="12" r="9.5" fill="#fff" />
      <path d="M2.5 12h19" stroke="#000" strokeWidth="1.7" />
      <ellipse
        cx="12"
        cy="12"
        rx="4.3"
        ry="9.5"
        fill="none"
        stroke="#000"
        strokeWidth="1.7"
      />
    </mask>
    <circle cx="12" cy="12" r="9.5" fill="currentColor" mask="url(#fi-globe)" />
  </svg>
)

const Chevron = ({ open }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform 0.15s ease",
    }}
  >
    <path d="M6 9.5l6 6 6-6" />
  </svg>
)
Chevron.propTypes = { open: PropTypes.bool }

const PinIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
    <circle cx="12" cy="10" r="2.3" />
  </svg>
)

// A doctor can be in two places on the same day, so each location gets its
// own heading above its times; video slots carry no location.
const groupByLocation = (slots) => {
  const groups = []
  for (const slot of slots) {
    const key = slot.location || null
    const group = groups.find(([location]) => location === key)
    if (group) group[1].push(slot)
    else groups.push([key, [slot]])
  }
  return groups
}

const SearchIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </svg>
)

const useStyles = makeStyles(() => ({
  wrap: { position: "relative" },
  light: {
    "& $callTitle, & $month, & $dayHead": { color: "#111" },
    "& $zoneBtn": { color: "#444" },
    "& $zonePanel": { backgroundColor: "#fff", borderColor: "#cfc8bf" },
    "& $zoneSearchRow": { borderBottomColor: "#ebe6df" },
    "& $zoneSearch": { color: "#111" },
    "& $zoneRow": { color: "#222" },
    "& $zoneRowOn": { backgroundColor: "#ebe2d6" },
    "& $weekday, & $zoneGroup, & $zoneNow, & $timesHint": { color: "#6b6560" },
    "& $cell": { color: "#b8b2ab" },
    "& $cellOpen": {
      color: "#111",
      backgroundColor: "#ebe2d6",
      borderColor: "#d3c4b1",
    },
    "& $cellSelected": {
      color: "#fff",
      backgroundColor: "#3a2618",
      borderColor: "#3a2618",
    },
    "& $navBtn": { borderColor: "#cfc8bf", color: "#333" },
    "& $timeBtn": {
      backgroundColor: "#fff",
      borderColor: "#cfc8bf",
      color: "#111",
    },
    "& $location": { color: "#333" },
    "& $timeBtnOn$timeBtnOn": {
      color: "#fff",
      backgroundColor: "#3a2618",
      borderColor: "#3a2618",
    },
    "& $overlayText": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderColor: "#cfc8bf",
      color: "#222",
    },
    "& $overlayBtn": {
      color: "#fff",
      borderColor: "#3a2618",
      background: "#3a2618",
    },
  },
  meta: { marginBottom: 14 },
  // Desktop: calendar and times side by side so the whole step fits on one
  // screen without shrinking the type. Phones stack them.
  body: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(0, 1fr)",
    gap: 28,
    alignItems: "start",
    "@media (max-width:760px)": { gridTemplateColumns: "1fr", gap: 8 },
  },
  bodySingle: { gridTemplateColumns: "1fr" },
  gridWrap: { position: "relative" },
  timesCol: {
    maxHeight: 400,
    overflowY: "auto",
    paddingRight: 4,
    "@media (max-width:760px)": { maxHeight: "none", overflowY: "visible" },
  },
  callTitle: {
    color: "white",
    fontSize: "1.15rem",
    fontWeight: 600,
    marginBottom: 10,
  },
  zoneBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 10px 6px 0",
    border: "none",
    background: "none",
    color: "#cfcccf",
    fontFamily: "inherit",
    fontSize: "1.05rem",
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": { color: "#fff" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  },
  zonePanel: {
    marginTop: 10,
    border: "1px solid #3a3a3a",
    borderRadius: 14,
    backgroundColor: "#141414",
    overflow: "hidden",
  },
  zoneSearchRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "11px 14px",
    borderBottom: "1px solid #2e2e2e",
    color: "#8a8686",
  },
  zoneSearch: {
    flex: 1,
    minWidth: 0,
    border: "none",
    background: "none",
    outline: "none",
    color: "white",
    fontFamily: "inherit",
    fontSize: "0.9rem",
    "&::placeholder": { color: "#8a8686", opacity: 1 },
  },
  zoneList: { maxHeight: 280, overflowY: "auto" },
  zoneEmpty: { padding: "16px 14px", color: "#8a8686", fontSize: "0.85rem" },
  zoneGroup: {
    padding: "10px 14px 4px",
    color: "#8a8686",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontSize: "0.65rem",
    fontWeight: 600,
  },
  zoneRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    width: "100%",
    padding: "9px 14px",
    border: "none",
    background: "none",
    color: "#d9d5d0",
    fontFamily: "inherit",
    fontSize: "0.85rem",
    textAlign: "left",
    cursor: "pointer",
    "&:hover": { backgroundColor: "rgba(239, 234, 226, 0.06)" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: -2 },
  },
  zoneRowOn: {
    color: "#f2efe9",
    backgroundColor: "rgba(239, 234, 226, 0.09)",
  },
  zoneNow: { color: "#8a8686", whiteSpace: "nowrap" },
  dimmed: {
    filter: "blur(1.5px)",
    opacity: 0.7,
    userSelect: "none",
    pointerEvents: "none",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  month: {
    color: "white",
    fontSize: "1.05rem",
    fontWeight: 600,
    textTransform: "capitalize",
  },
  navRow: { display: "flex", gap: 8 },
  navBtn: {
    width: 38,
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #3a3a3a",
    borderRadius: 10,
    backgroundColor: "transparent",
    color: "#d9d5d0",
    fontFamily: "inherit",
    fontSize: "1rem",
    cursor: "pointer",
    transition: "border-color 0.15s ease",
    "&:hover:not(:disabled)": { borderColor: ACCENT },
    "&:disabled": { opacity: 0.3, cursor: "default" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 },
  weekday: {
    color: "#8a8686",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontSize: "0.75rem",
    fontWeight: 600,
    textAlign: "center",
    padding: "4px 0 8px",
  },
  cell: {
    position: "relative",
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid transparent",
    borderRadius: 12,
    backgroundColor: "transparent",
    color: "#5f5b59",
    fontFamily: "inherit",
    fontSize: "1.1rem",
    fontWeight: 500,
    padding: 0,
  },
  cellOpen: {
    color: "#e4e0db",
    backgroundColor: "rgba(239, 234, 226, 0.09)",
    borderColor: "rgba(185, 178, 166, 0.35)",
    cursor: "pointer",
    transition: "border-color 0.15s ease, color 0.15s ease",
    "&:hover": { borderColor: ACCENT, color: "#fff" },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  },
  cellSelected: {
    color: "#f2efe9",
    backgroundColor: "rgba(239, 234, 226, 0.22)",
    borderColor: "#b9b2a6",
    fontWeight: 600,
  },
  todayDot: {
    position: "absolute",
    bottom: 6,
    left: "50%",
    transform: "translateX(-50%)",
    width: 4,
    height: 4,
    borderRadius: "50%",
    backgroundColor: "#b9b2a6",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    textAlign: "center",
  },
  overlayText: {
    backgroundColor: "rgba(20,20,20,0.94)",
    border: "1px solid #3a3a3a",
    borderRadius: 16,
    padding: "14px 20px",
    color: "#e4e0db",
    fontSize: "1rem",
    lineHeight: 1.5,
    maxWidth: 340,
  },
  dayHead: {
    color: "white",
    fontSize: "1.05rem",
    fontWeight: 600,
    marginTop: 4,
    marginBottom: 12,
    textTransform: "capitalize",
    "@media (max-width:760px)": { marginTop: 18 },
  },
  times: { display: "grid", gap: 10 },
  // "&&" so it wins over timeBtn, which is declared later in this sheet.
  timeBtnOn: {
    "&&": {
      backgroundColor: "rgba(239, 234, 226, 0.22)",
      borderColor: "#b9b2a6",
      fontWeight: 600,
    },
  },
  locationGroup: { "& + &": { marginTop: 18 } },
  location: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#e4e0db",
    fontSize: "1rem",
    fontWeight: 500,
    marginBottom: 10,
  },
  timesHint: { color: "#8a8686", fontSize: "1rem", marginTop: 4 },
  overlayBtn: {
    marginTop: 12,
    padding: "10px 18px",
    border: "1px solid #b9b2a6",
    borderRadius: 999,
    background: "rgba(239, 234, 226, 0.09)",
    color: "#f2efe9",
    fontFamily: "inherit",
    fontSize: "0.95rem",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": { borderColor: ACCENT },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  },
  timeBtn: {
    width: "100%",
    padding: "13px 20px",
    border: "1px solid #3a3a3a",
    borderRadius: 999,
    backgroundColor: "#171717",
    color: "white",
    fontSize: "1.1rem",
    fontWeight: 500,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "border-color 0.15s ease",
    "&:hover": { borderColor: ACCENT },
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 3 },
  },
}))

const SlotCalendar = ({
  locale,
  locked,
  slots,
  onPick,
  callTitle,
  zoneLabel,
  zoneSearchLabel,
  zoneNoResults,
  lockedMessage,
  monthFullMessage,
  bookedUntilMessage,
  noTimesMessage,
  firstAvailableLabel,
  chooseDayMessage,
  prevLabel,
  nextLabel,
  light = false,
  selectedId = null,
}) => {
  const classes = useStyles()
  // "Today" and the zone are read after mount: the page is built once and
  // served for weeks, so a build-time date would be wrong and would not match
  // the first client render.
  const [today, setToday] = useState(null)
  const [cursor, setCursor] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  const [timeZone, setTimeZone] = useState("Europe/Amsterdam")
  const [zoneOpen, setZoneOpen] = useState(false)
  const [zoneQuery, setZoneQuery] = useState("")

  useEffect(() => {
    const now = new Date()
    setToday(now)
    setCursor(startOfMonth(now))
    setTimeZone(detectZone())
  }, [])

  // Days are grouped in the zone the times are shown in, so a 23:30 Amsterdam
  // slot sits on the next day for a patient in Asia.
  const byDay = useMemo(() => {
    const map = {}
    ;(slots || []).forEach((s) => {
      const k = dayKeyIn(new Date(s.start), timeZone)
      if (!map[k]) map[k] = []
      map[k].push(s)
    })
    return map
  }, [slots, timeZone])

  // Jump to whichever month actually holds availability, and preselect the
  // first open day so the times are on screen without another tap.
  useEffect(() => {
    if (!slots || slots.length === 0) return
    const firstKey = dayKeyIn(new Date(slots[0].start), timeZone)
    setCursor(monthOfKey(firstKey))
    setSelectedDay(firstKey)
  }, [slots, timeZone])

  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" })
    return [0, 1, 2, 3, 4, 5, 6].map((i) =>
      fmt.format(new Date(2023, 0, 1 + i))
    )
  }, [locale])

  // Matched against the visible label, the group and the raw IANA name, so
  // "los angeles" finds the zone whose label only says "Pacific Time".
  const zoneMatches = useMemo(() => {
    const q = zoneQuery.trim().toLowerCase()
    if (!q) return TIME_ZONE_GROUPS
    return TIME_ZONE_GROUPS.map(({ group, zones }) => ({
      group,
      zones: zones.filter(
        ([id, label]) =>
          label.toLowerCase().includes(q) ||
          group.toLowerCase().includes(q) ||
          id.toLowerCase().replace(/_/g, " ").includes(q)
      ),
    })).filter((g) => g.zones.length > 0)
  }, [zoneQuery])

  const placeholderDays = useMemo(() => {
    const out = {}
    if (!today) return out
    for (let i = 0; i <= 3; i += 1) {
      const d = new Date(today)
      d.setDate(d.getDate() + i)
      out[dayKey(d)] = true
    }
    return out
  }, [today])

  if (!today || !cursor)
    return <div className={`${classes.wrap} ${light ? classes.light : ""}`} />

  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(cursor)

  const fmtTime = (iso) =>
    new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    }).format(new Date(iso))

  const fmtDayHead = (key) =>
    new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date(`${key}T00:00:00`))

  const nowIn = (zone) =>
    new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: zone,
    }).format(today)

  // While locked there is nothing real to show, so the next few days carry the
  // tint purely to give the blurred grid its shape.
  const openDays = locked ? placeholderDays : byDay

  const cells = []
  const first = startOfMonth(cursor)
  for (let i = 0; i < first.getDay(); i += 1) cells.push(null)
  for (let d = 1; d <= daysInMonth(cursor); d += 1) {
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d))
  }

  const shiftMonth = (delta) =>
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1))

  const monthPrefix = dayKey(cursor).slice(0, 7)
  const openKeys = Object.keys(byDay).sort()
  const monthHasTimes = openKeys.some((k) => k.startsWith(monthPrefix))
  const firstLater = openKeys.find((k) => k.slice(0, 7) > monthPrefix)
  const loaded = !locked && Array.isArray(slots)
  const noTimesAtAll = loaded && slots.length === 0
  const monthFull = loaded && !noTimesAtAll && !monthHasTimes
  const showDay = selectedDay && selectedDay.startsWith(monthPrefix)
  const dayTimes = showDay ? byDay[selectedDay] || [] : []

  // "Wednesday 14 October" rather than the American "Wednesday, October 14".
  const fmtLongDay = (key) =>
    new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date(`${key}T00:00:00`))

  const jumpTo = (key) => {
    setCursor(monthOfKey(key))
    setSelectedDay(key)
  }

  return (
    <div className={`${classes.wrap} ${light ? classes.light : ""}`}>
      {!locked && (
        <div className={classes.meta}>
          {callTitle && (
            <Typography className={classes.callTitle}>{callTitle}</Typography>
          )}
          <button
            type="button"
            className={classes.zoneBtn}
            onClick={() => {
              setZoneOpen((v) => !v)
              setZoneQuery("")
            }}
            aria-expanded={zoneOpen}
            aria-label={zoneLabel}
          >
            <GlobeIcon />
            {`${zoneHeading(timeZone)} (${nowIn(timeZone)})`}
            <Chevron open={zoneOpen} />
          </button>
          {zoneOpen && (
            <div className={classes.zonePanel}>
              <div className={classes.zoneSearchRow}>
                <SearchIcon />
                <input
                  className={classes.zoneSearch}
                  placeholder={zoneSearchLabel}
                  aria-label={zoneSearchLabel}
                  value={zoneQuery}
                  onChange={(e) => setZoneQuery(e.target.value)}
                />
              </div>
              <div className={classes.zoneList}>
                {zoneMatches.map(({ group, zones }) => (
                  <div key={group}>
                    <div className={classes.zoneGroup}>{group}</div>
                    {zones.map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className={`${classes.zoneRow} ${
                          id === timeZone ? classes.zoneRowOn : ""
                        }`}
                        onClick={() => {
                          setTimeZone(id)
                          setZoneOpen(false)
                          setZoneQuery("")
                        }}
                      >
                        <span>{label}</span>
                        <span className={classes.zoneNow}>{nowIn(id)}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {zoneMatches.length === 0 && (
                  <div className={classes.zoneEmpty}>{zoneNoResults}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={classes.head}>
        <Typography className={classes.month}>{monthLabel}</Typography>
        <div className={locked ? classes.dimmed : undefined}>
          <div className={classes.navRow}>
            <button
              type="button"
              className={classes.navBtn}
              onClick={() => shiftMonth(-1)}
              disabled={sameMonth(cursor, today)}
              aria-label={prevLabel}
            >
              ‹
            </button>
            <button
              type="button"
              className={classes.navBtn}
              onClick={() => shiftMonth(1)}
              aria-label={nextLabel}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div
        className={`${classes.body} ${
          locked || noTimesAtAll ? classes.bodySingle : ""
        }`}
      >
        <div className={classes.gridWrap}>
          <div
            className={`${classes.grid} ${
              locked || monthFull || noTimesAtAll ? classes.dimmed : ""
            }`}
            role="presentation"
          >
            {weekdays.map((w, i) => (
              <div key={`${w}-${i}`} className={classes.weekday}>
                {w}
              </div>
            ))}
            {cells.map((d, i) => {
              if (!d) return <div key={`pad-${i}`} className={classes.cell} />
              const key = dayKey(d)
              const isOpen = Boolean(openDays[key])
              const isSelected = selectedDay === key
              const isToday = key === dayKeyIn(today, timeZone)
              const cls = `${classes.cell} ${isOpen ? classes.cellOpen : ""} ${
                isSelected ? classes.cellSelected : ""
              }`
              return isOpen ? (
                <button
                  key={key}
                  type="button"
                  className={cls}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedDay(key)}
                >
                  {d.getDate()}
                  {isToday && !isSelected && (
                    <span className={classes.todayDot} />
                  )}
                </button>
              ) : (
                <div key={key} className={cls}>
                  {d.getDate()}
                  {isToday && <span className={classes.todayDot} />}
                </div>
              )
            })}
          </div>
          {(monthFull || noTimesAtAll) && (
            <div className={classes.overlay}>
              <div className={classes.overlayText}>
                {noTimesAtAll
                  ? noTimesMessage
                  : firstLater && bookedUntilMessage
                  ? bookedUntilMessage(
                      fmtLongDay(firstLater),
                      byDay[firstLater][0]
                    )
                  : monthFullMessage}
                {monthFull && firstLater && (
                  <div>
                    <button
                      type="button"
                      className={classes.overlayBtn}
                      onClick={() => jumpTo(firstLater)}
                    >
                      {firstAvailableLabel}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!locked && !noTimesAtAll && (
          <div className={classes.timesCol}>
            {dayTimes.length > 0 ? (
              <>
                <Typography className={classes.dayHead}>
                  {fmtDayHead(selectedDay)}
                </Typography>
                {groupByLocation(dayTimes).map(([location, times]) => (
                  <div
                    key={location || "video"}
                    className={classes.locationGroup}
                  >
                    {location && (
                      <Typography className={classes.location}>
                        <PinIcon />
                        {location}
                      </Typography>
                    )}
                    <div className={classes.times}>
                      {times.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`${classes.timeBtn} ${
                            s.id === selectedId ? classes.timeBtnOn : ""
                          }`}
                          aria-pressed={s.id === selectedId}
                          onClick={() => onPick(s)}
                        >
                          {fmtTime(s.start)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              !monthFull && (
                <Typography className={classes.timesHint}>
                  {chooseDayMessage}
                </Typography>
              )
            )}
          </div>
        )}
      </div>

      {locked && (
        <div className={classes.overlay}>
          <Typography className={classes.overlayText}>
            {lockedMessage}
          </Typography>
        </div>
      )}
    </div>
  )
}

SlotCalendar.propTypes = {
  locale: PropTypes.string.isRequired,
  locked: PropTypes.bool,
  slots: PropTypes.array,
  callTitle: PropTypes.string,
  zoneLabel: PropTypes.string,
  zoneSearchLabel: PropTypes.string,
  zoneNoResults: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  lockedMessage: PropTypes.string,
  monthFullMessage: PropTypes.string,
  bookedUntilMessage: PropTypes.func,
  noTimesMessage: PropTypes.string,
  firstAvailableLabel: PropTypes.string,
  chooseDayMessage: PropTypes.string,
  prevLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  light: PropTypes.bool,
  selectedId: PropTypes.string,
}

export default SlotCalendar
